import {
  PROPOSTA_SITUACAO,
  CreateProposalInputDTO,
  UpdateProposalInputDTO,
  TIPO_PESSOA
} from "@repo/core/services/entities/Proposals";
import { ProposalTypeByCode } from "@repo/core/constants/proposals";
import { LimitReassessmentEnum } from "@repo/core/enums/proposals";
import { getCleanCPF } from "@repo/core/utils/string.utils";
import { PreProposalData } from "../../store/preProposalStore";
import { ProposalFormData } from "./types";
import { sanitizePreProposalApiPayload } from "../../services/sanitizePreProposalApiPayload.service";

/**
 * Creates full phone number from DDD and phone
 */
export const createFullPhone = (phoneDDD: string, phone: string): string => {
  return phoneDDD && phone ? `${phoneDDD}${phone}` : "";
};

/**
 * Splits full phone number into DDD and phone number
 */
export const splitPhoneNumber = (
  fullPhone: string
): { phoneDDD: string; phone: string } => {
  if (!fullPhone || fullPhone.length < 11) {
    return { phoneDDD: "", phone: "" };
  }
  return {
    phoneDDD: fullPhone.slice(0, 2),
    phone: fullPhone.slice(2)
  };
};

/**
 * Extracts phone data from cellPhone string and builds ProposalFormData from store
 */
export const buildProposalFormDataFromPhone = (
  cellPhone: string,
  proposalData: PreProposalData
): ProposalFormData => {
  const cleanPhone = cellPhone.replace(/\D/g, "");
  const { phoneDDD, phone } = splitPhoneNumber(cleanPhone);
  const cleanCPF = proposalData.strCliente
    ? getCleanCPF(proposalData.strCliente)
    : "";
  const fullName = proposalData.strNome || "";

  return {
    phoneDDD,
    phone,
    cleanCPF,
    fullName
  };
};

/**
 * Maps form data and proposal data to CreateProposalInputDTO for normal proposal creation
 */
export const mapToCreateProposalInput = (
  formData: ProposalFormData,
  proposalData: PreProposalData | undefined
): CreateProposalInputDTO => {
  return {
    strSituacao: PROPOSTA_SITUACAO.PRE_PROPOSAL,
    strTipoProposta: ProposalTypeByCode.AMBOS,
    strNome: formData.fullName,
    strCliente: formData.cleanCPF,
    strEndFone1: formData.phone,
    strEndDdd1: formData.phoneDDD,
    strAtendente: proposalData?.strAtendente || "",
    strProduto: proposalData?.strProduto || "",
    strPontoAtendimento: proposalData?.strPontoAtendimento || "",
    strBandeira: proposalData?.strBandeira,
    blnManterPendente: true,

    // Fixed fields
    strTipoPessoa: TIPO_PESSOA.FISICA,
    strReavaliacaoLimite: LimitReassessmentEnum.ANALYSIS
  };
};

/**
 * Maps form data and proposal data to UpdateProposalInputDTO for updating an existing proposal.
 * Spreads the FULL store snapshot (minus local-only UI fields) so every Proposta/Alterar
 * call mirrors the original Proposta/Incluir payload — matching the spec requirement that
 * all fields must be sent even when not yet filled.
 */
export const mapToUpdatePreProposalInput = (
  formData: ProposalFormData,
  proposalId: number,
  proposalData: PreProposalData | undefined
): UpdateProposalInputDTO => {
  const sanitized = sanitizePreProposalApiPayload(proposalData);

  return {
    ...(sanitized as unknown as UpdateProposalInputDTO),
    lngProposta: proposalId.toString(),
    strNome: formData.fullName,
    strCliente: formData.cleanCPF,
    strEndFone1: formData.phone,
    strEndDdd1: formData.phoneDDD,
    strSituacao: proposalData?.strSituacao || PROPOSTA_SITUACAO.DATA_CONFERENCE,
    blnManterPendente: true,
    strTipoPessoa: TIPO_PESSOA.FISICA
  };
};
